/**
 * Shareable lobby links: `https://example.com/?join=7MWT`.
 *
 * A join code typed into a box is fine at a desk and hopeless in a chat window,
 * so a lobby is also a URL. Opening one connects and walks straight into that
 * lobby; the address bar mirrors whichever lobby you are in, so sharing is
 * "copy the URL" even for someone who never notices the code.
 *
 * Pure string work, kept out of the UI so it can be tested without a browser.
 */

/** Query parameter carrying the lobby code. */
export const JOIN_PARAM = 'join';

/**
 * Shape of a join code, matching what the server generates: 4–5 characters from
 * an unambiguous uppercase alphabet (server/codes.ts).
 */
const CODE_RE = /^[A-Z0-9]{4,5}$/;

/**
 * The lobby code in a query string, or null when there isn't a usable one.
 * Case and stray whitespace are forgiven — links get retyped and mangled by
 * chat clients — but nothing code-shaped is invented out of junk.
 */
export function parseJoinCode(search: string): string | null {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return null;
  }
  const raw = params.get(JOIN_PARAM);
  if (raw === null) return null;
  const code = raw.trim().toUpperCase();
  return CODE_RE.test(code) ? code : null;
}

/**
 * The link that drops someone into `code`, built from the page they are on.
 *
 * The existing query and hash are dropped so a stale `?join=` from a previous
 * lobby cannot survive — except `?server=`, which is not decoration: it is how a
 * client is pointed at a server that is not the one serving the page, and a link
 * without it would send the recipient somewhere else entirely.
 */
export function buildJoinUrl(href: string, code: string): string {
  const url = new URL(href);
  const server = url.searchParams.get('server');
  url.search = '';
  url.hash = '';
  url.searchParams.set(JOIN_PARAM, code.toUpperCase());
  if (server) url.searchParams.set('server', server);
  return url.toString();
}

/** The same page with any join code stripped, for when a player leaves. */
export function clearJoinParam(href: string): string {
  const url = new URL(href);
  url.searchParams.delete(JOIN_PARAM);
  return url.toString();
}
